/**
 * ConditionEditor Component
 * 
 * A complex component that provides a complete interface for editing Azure Policy conditions.
 * It allows users to build and modify policy conditions through a user-friendly interface.
 * 
 * Features:
 * - Field selection with searchable dropdown and resource type grouping
 * - Operator selection based on field type
 * - Dynamic value input with context-aware suggestions
 * - NOT operator toggle for negating conditions
 * - Support for both simple and complex conditions
 * 
 * @component
 */

import React, { useRef, useMemo, RefObject } from 'react';
import { AVAILABLE_FIELDS, OPERATOR_PLACEHOLDERS, COMMON_FIELDS, GROUPED_ALIASES } from '../../constants/policy';
import type { SimpleCondition, NotCondition } from '../../types/policy';
import { useCondition } from './hooks/useCondition';
import { useDropdown } from './hooks/useDropdown';
import { OperatorSelect } from './components/OperatorSelect';
import { SearchableInput } from './components/SearchableInput';
import { Dropdown } from './components/Dropdown';
import type { ConditionEditorProps } from './types';

// Constants for dropdown pagination
const ITEMS_PER_PAGE = 50;
const VALUE_ITEMS_PER_PAGE = 50;

/**
 * ConditionEditor component allows editing of a policy condition.
 * Provides fields for selecting the condition field, operator, and value,
 * including a toggle for negation (NOT).
 * @param {ConditionEditorProps} props - The component props.
 * @returns {JSX.Element} The rendered ConditionEditor component.
 */
export const ConditionEditor: React.FC<ConditionEditorProps> = ({ condition, onUpdate }) => {
  // Refs for input elements to manage focus and positioning
  const inputRef = useRef<HTMLInputElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);

  // Initialize condition state management
  const conditionState = useCondition(condition, onUpdate);

  // Initialize dropdown states for field and value selection
  const fieldDropdown = useDropdown(
    inputRef as RefObject<HTMLInputElement>,
    ITEMS_PER_PAGE,
    AVAILABLE_FIELDS.length,
    conditionState.fieldOrValue
  );

  // Determine total items for value dropdown: show resource types if field is 'type'
  const totalValueItems = conditionState.fieldOrValue === 'type' ? GROUPED_ALIASES.length : 0;
  const valueDropdown = useDropdown(
    valueInputRef as RefObject<HTMLInputElement>,
    VALUE_ITEMS_PER_PAGE,
    totalValueItems,
    conditionState.value as string
  );

  // Render dropdown content for 'type' field values
  const renderTypeDropdownContent = () => {
    const filtered = valueDropdown.searchTerm
      ? GROUPED_ALIASES.filter(group =>
          group.resourceType.toLowerCase().includes(valueDropdown.searchTerm.toLowerCase())
        )
      : GROUPED_ALIASES;
    return (
      <Dropdown
        isOpen={valueDropdown.isOpen}
        position={valueDropdown.position}
        width={valueInputRef.current?.offsetWidth}
        onScroll={valueDropdown.handleScroll}
      >
        {filtered.slice(0, valueDropdown.displayCount).map(group => (
          <div key={group.resourceType}
               className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
               onMouseDown={() => {
                 conditionState.handleValueChange(group.resourceType);
                 valueDropdown.setCurrentValue(group.resourceType);
                 valueDropdown.setIsOpen(false);
                 valueDropdown.setSearchTerm('');
               }}
          >
            {group.resourceType}
          </div>
        ))}
        {valueDropdown.displayCount < filtered.length && (
          <div className="px-2 py-1 text-gray-500 text-center text-xs">Loading more...</div>
        )}
      </Dropdown>
    );
  };

  /**
   * Renders the value input field based on the selected field type (e.g., resource type dropdown or generic input).
   * @returns JSX.Element The rendered value input component
   */
  const renderValueInput = () => {
    const isTypeField = conditionState.fieldOrValue === 'type';
    if (isTypeField) {
      return (
        <SearchableInput
          inputRef={valueInputRef as RefObject<HTMLInputElement>}
          value={valueDropdown.currentValue}
          searchTerm={valueDropdown.searchTerm}
          isOpen={valueDropdown.isOpen}
          placeholder={OPERATOR_PLACEHOLDERS[conditionState.operator as keyof typeof OPERATOR_PLACEHOLDERS]}
          onChange={(value: string) => {
            conditionState.handleValueChange(value);
            valueDropdown.setCurrentValue(value);
          }}
          onSearchChange={valueDropdown.setSearchTerm}
          onFocus={() => valueDropdown.handleInputFocus()}
          onBlur={() => valueDropdown.handleBlur((value: string) => {
            conditionState.handleValueChange(value);
            valueDropdown.setCurrentValue(value);
          })}
          dropdownContent={renderTypeDropdownContent()}
        />
      );
    }
    // For non-type fields, use a textarea for multi-line value with matching input styles
    return (
      <textarea
        value={conditionState.value as string}
        onChange={(e) => conditionState.handleValueChange(e.target.value)}
        placeholder={OPERATOR_PLACEHOLDERS[conditionState.operator as keyof typeof OPERATOR_PLACEHOLDERS]}
        className="block w-full min-h-[80px] resize-y rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-2 text-sm text-gray-800 dark:text-gray-200 focus:border-blue-500 focus:ring-blue-500"
      />
    );
  };

  /**
   * Renders the dropdown content for field selection, grouping fields by category.
   * @returns JSX.Element The rendered dropdown content for fields.
   */
  const renderFieldDropdownContent = () => {
    const filteredFields = fieldDropdown.searchTerm
      ? AVAILABLE_FIELDS.filter(field => 
          field.display.toLowerCase().includes(fieldDropdown.searchTerm.toLowerCase())
        )
      : AVAILABLE_FIELDS;

    return (
      <Dropdown
        isOpen={fieldDropdown.isOpen}
        position={fieldDropdown.position}
        width={inputRef.current?.offsetWidth}
        onScroll={fieldDropdown.handleScroll}
      >
        {filteredFields
          .slice(0, fieldDropdown.displayCount)
          .map((field) => {
            return (
              <div
                key={field.value}
                className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                onMouseDown={() => {
                  conditionState.handleFieldChange(field.value);
                  fieldDropdown.setCurrentValue(field.display);
                  fieldDropdown.setIsOpen(false);
                  fieldDropdown.setSearchTerm('');
                }}
              >
                {field.display}
              </div>
            );
          })}
        {fieldDropdown.displayCount < filteredFields.length && (
          <div className="px-2 py-1 text-gray-500 text-center text-xs">Loading more...</div>
        )}
      </Dropdown>
    );
  };

  return (
    <div className="flex flex-col space-y-4 w-full bg-gray-50 dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* NOT operator toggle removed (handled at node level) */}

      {/* Field selection input with grouped dropdown */}
      <div className="w-full border-b border-gray-200 dark:border-gray-700 pb-2">
        <div className="flex justify-between items-center mb-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {conditionState.isValueCondition ? 'Value' : 'Field'}
          </label>
          <div className="flex items-center">
            <label htmlFor="value-toggle" className="mr-2 text-sm text-gray-600 dark:text-gray-400">Use Value</label>
            <input
              type="checkbox"
              id="value-toggle"
              checked={conditionState.isValueCondition}
              onChange={conditionState.handleValueConditionToggle}
              className="form-checkbox h-5 w-5 text-blue-600 rounded"
            />
          </div>
        </div>
        <SearchableInput
          inputRef={inputRef as RefObject<HTMLInputElement>}
          value={fieldDropdown.currentValue}
          searchTerm={fieldDropdown.searchTerm}
          isOpen={fieldDropdown.isOpen}
          placeholder={conditionState.isValueCondition ? "e.g. [field('Microsoft.Web/sites/publicNetworkAccess')]" : "Select field..."}
          onChange={(value: string) => {
            conditionState.handleFieldChange(value);
            fieldDropdown.setCurrentValue(value);
          }}
          onSearchChange={fieldDropdown.setSearchTerm}
          onFocus={fieldDropdown.handleInputFocus}
          onBlur={() => fieldDropdown.handleBlur((value: string) => {
            conditionState.handleFieldChange(value);
            fieldDropdown.setCurrentValue(value);
          })}
          dropdownContent={renderFieldDropdownContent()}
        />
      </div>

      {/* Operator selection dropdown */}
      <div className="w-full border-b border-gray-200 dark:border-gray-700 pb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Operator</label>
        <OperatorSelect
          value={conditionState.operator}
          onChange={conditionState.handleOperatorChange}
        />
      </div>

      {/* Value input field with context-aware suggestions */}
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Value</label>
        {renderValueInput()}
      </div>
    </div>
  );
};

export default ConditionEditor;